export interface DebouncedFunction<A extends unknown[]> {
  (...args: A): void;
  /** 대기 중인 호출을 취소한다. 소유자(effect 등)의 정리 시점에 부른다. */
  cancel: () => void;
}

/**
 * 트레일링 디바운스.
 * 반환된 함수를 연속 호출하면 **마지막 호출로부터** `delayMs` 가 조용히 지난 뒤 한 번만 실행된다.
 *
 * `delayMs` 가 0 이하면 디바운스를 끄고 즉시 실행한다.
 * `setTimeout(0)` 이 아니라 동기 실행이라 태스크 큐를 한 틱도 거치지 않는다.
 */
export const debounce = <A extends unknown[]>(run: (...args: A) => void, delayMs: number): DebouncedFunction<A> => {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: A): void => {
    if (timer !== null) clearTimeout(timer);

    if (delayMs <= 0) {
      timer = null;
      run(...args);
      return;
    }

    timer = setTimeout(() => {
      timer = null;
      run(...args);
    }, delayMs);
  };

  debounced.cancel = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  return debounced;
};
